class CollectionProductsGrid extends HHBaseElement {
  loading = false

  constructor() {
    super()
    this.$grid = this.querySelector('.collection-grid')
    this.$backdrop = this.querySelector('.js-backdrop')
    this._handleLoadMore = this._handleLoadMore.bind(this)
    this._handleFiltersChange = this._handleFiltersChange.bind(this)
    this._handleFilterFormSubmit = this._handleFilterFormSubmit.bind(this)
    this._handleClearAllClick = this._handleClearAllClick.bind(this)
    this._handleLocationPopState = this._handleLocationPopState.bind(this)
    this.$filtersForm = this.querySelector('[data-filters-form]')
    this.$filtersFormSubmitable = this.querySelector(
      '[data-filter-form-submitable]'
    )
    this.$slideoutContainer = document.querySelector(
      `[slideout-id="mobile-filters"]`
    )
  }

  connectedCallback() {
    // Infinite scroll appends pages, so a restored scroll offset points at content that no longer exists.
    if (this.infiniteScroll && 'scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual'
    }
    this._attachListeners()
    this._setLoadMoreListners()
  }

  disconnectedCallback() {
    this._removeListeners()
    this.loadMoreObserver?.disconnect()
  }

  get infiniteScroll() {
    return this.hasAttribute('data-infinite-scroll')
  }

  get nextPageUrl() {
    return this.getAttribute('data-paginate-next')
  }

  set nextPageUrl(value) {
    // setAttribute stringifies a null next/previous page into a truthy "null".
    if (value) {
      this.setAttribute('data-paginate-next', value)
    } else {
      this.removeAttribute('data-paginate-next')
    }
  }

  get previousPageUrl() {
    return this.getAttribute('data-paginate-previous')
  }

  set previousPageUrl(value) {
    // setAttribute stringifies a null next/previous page into a truthy "null".
    if (value) {
      this.setAttribute('data-paginate-previous', value)
    } else {
      this.removeAttribute('data-paginate-previous')
    }
  }

  async _fetchPage(nextUrl) {
    if (nextUrl) {
      const response = await fetch(nextUrl)
      const $page = document.createElement('div')
      $page.innerHTML = await response.text()
      const collectionGrid = $page.querySelector('collection-products-grid')
      return {
        products: $page.querySelectorAll(
          'collection-products-grid #collection-products-grid > *:not([data-load-more])'
        ),
        loadMoreButton: $page.querySelector(
          'collection-products-grid #collection-products-grid .collection-grid__load-more'
        ),
        paginateNext: collectionGrid.nextPageUrl,
        paginatePrevious: collectionGrid.previousPageUrl,
        filterContent: $page.querySelector('[data-filters-form]'),
        mobileFilterContent: $page.querySelector(
          '[data-filter-form-submitable]'
        ),
      }
    }

    throw new Error('No More Page to Load')
  }

  _addProductsAndCards(contents = []) {
    if (this.$loadMoreContainer) {
      this.$loadMoreContainer.before(...contents)
    } else {
      this.$grid.append(...contents)
    }
  }

  _renderPage(page, reset = false) {
    this.nextPageUrl = page.paginateNext
    this.previousPageUrl = page.paginatePrevious
    if (reset) {
      this.$grid.innerHTML = ''
      for (const item of page.products) {
        this.$grid.append(item)
      }
    } else {
      this._addProductsAndCards(page.products)
    }

    if (page.filterContent) {
      const currentOpenMenu = this.$filtersForm.querySelector(
        'collection-filter-dropdown[open]'
      )
      if (currentOpenMenu?.id) {
        page.filterContent
          .querySelector(`#${currentOpenMenu.id}`)
          .setAttribute('open', 'true')
      }
      this.$filtersForm.innerHTML = page.filterContent.innerHTML
      this.$filtersFormSubmitable.innerHTML = page.mobileFilterContent.innerHTML
    }

    if (page.loadMoreButton) {
      this.$loadMoreContainer.remove()
      this.$grid.append(page.loadMoreButton)
      this._setLoadMoreListners()
    }
  }

  _handleLoadMore(event) {
    event.preventDefault()
    return this._loadNextPage()
  }

  async _loadNextPage(silent = false) {
    // Outside the try: an early return here must not reach the finally and clear the
    // flag while the in-flight fetch is still running.
    if (this.loading) {
      return
    }

    try {
      this._setLoading(true, silent)
      this.$loadMore?.setAttribute('disabled', true)
      const nextPage = await this._fetchPage(this.nextPageUrl)
      this._renderPage(nextPage)
    } catch (error) {
      console.error(error)
      // The observer will not fire again while the container is already intersecting,
      // so a failed scroll load has to fall back to a control the shopper can use.
      this.loadMoreObserver?.disconnect()
      if (this.$loadMore) {
        this.$loadMore.removeAttribute('disabled')
        this.$loadMore.style.removeProperty('display')
      }
    } finally {
      this._setLoading(false)
    }
  }

  _setLoadMoreListners() {
    if (this.$loadMore) {
      this.$loadMore.removeEventListener('click', this._handleLoadMore)
    }

    this.$loadMore = this.querySelector('[data-load-more]')
    this.$loadMoreContainer = this.querySelector('.collection-grid__load-more')

    if (this.$loadMore) {
      this.$loadMore.addEventListener('click', this._handleLoadMore)

      if (this.infiniteScroll) {
        // Kept in the markup as the crawlable / no-JS path to the next page.
        // Inline style because `.button` sets `display: inline-flex`, which beats `[hidden]`.
        this.$loadMore.style.display = 'none'
      }
    }

    this.loadMoreObserver?.disconnect()
    if (this.infiniteScroll && this.$loadMoreContainer && this.nextPageUrl) {
      this.loadMoreObserver = new IntersectionObserver(
        (entries) => {
          if (
            entries.some((entry) => entry.isIntersecting) &&
            this.nextPageUrl
          ) {
            this._loadNextPage(true)
          }
        },
        { rootMargin: '400px 0px' }
      )
      this.loadMoreObserver.observe(this.$loadMoreContainer)
    }
  }

  get filterFormData() {
    return new FormData(
      this._submitedFilterForm ? this.$filtersFormSubmitable : this.$filtersForm
    )
  }

  get filterQueryString() {
    return this._clearAll
      ? new URLSearchParams({
          sort_by: this.filterFormData.get('sort_by'),
        }).toString()
      : new URLSearchParams(this.filterFormData).toString()
  }

  async _handleFiltersChange(_, queryString) {
    try {
      if (this.$slideoutContainer) {
        this.$slideoutContainer.open = false
      }

      this._setLoading(true)
      if (!queryString) {
        window.history.pushState(
          {},
          '',
          `${window.location.pathname}?${this.filterQueryString}`
        )
      }
      const pageData = await this._fetchPage(
        `${window.location.pathname}?${queryString ?? this.filterQueryString}`
      )

      this.triggerEvent('change::filters')
      this._renderPage(pageData, true)
    } catch (error) {
      console.log(error)
    } finally {
      this._setLoading(false)
      this._submitedFilterForm = false
      this._clearAll = false
      if (this.$slideoutContainer) {
        this.$slideoutContainer.open = false
      }
    }
  }

  _handleFilterFormSubmit(event) {
    event.preventDefault()
    this._submitedFilterForm = true
    this._handleFiltersChange()
  }

  _handleClearAllClick(event) {
    if (event.target.hasAttribute('data-clear-all')) {
      event.preventDefault()
      this._clearAll = true
      this._handleFiltersChange()
    }
  }

  _handleLocationPopState(event) {
    this._handleFiltersChange(event, window.location.search.split('?')[1])
  }

  _attachListeners() {
    this.$filtersForm?.addEventListener('change', this._handleFiltersChange)
    this.$filtersFormSubmitable?.addEventListener(
      'submit',
      this._handleFilterFormSubmit
    )
    this.addEventListener('click', this._handleClearAllClick)
    this.addEventListener(
      'filter-by-image:change',
      this._handleFilterByImageChange
    )
    window.addEventListener('popstate', this._handleLocationPopState)
  }

  _removeListeners() {
    this.$filtersForm.removeEventListener('change', this._handleFiltersChange)
    this.$filtersFormSubmitable.removeEventListener(
      'submit',
      this._handleFilterFormSubmit
    )
    this.removeEventListener('click', this._handleClearAllClick)
    this.removeEventListener(
      'filter-by-image:change',
      this._handleFilterByImageChange
    )
    window.removeEventListener('popstate', this._handleLocationPopState)
  }

  _handleFilterByImageChange = (e) => {
    this._handleFiltersChange(null, e.detail.filterValue)
    window.history.pushState(
      {},
      '',
      `${window.location.pathname}?${e.detail.filterValue}`
    )
  }

  _setLoading(loading, silent = false) {
    this.loading = loading
    // Scroll-triggered loads are background work - no full-viewport scrim over the grid.
    this.silentLoading = loading && silent
    this._applyState()
  }

  _applyState() {
    this.$backdrop.classList[
      this.loading && !this.silentLoading ? 'add' : 'remove'
    ]('visible')
    this.classList[this.loading ? 'add' : 'remove']('loading')
  }
}

window.customElements.define('collection-products-grid', CollectionProductsGrid)
